import React from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIIconButton from '@/components/shared/UIIconButton';
import { type IProperty } from '@/types';
import './PropertyInformation.scss';

export interface PropertyInformationProps {
  /**
   * Об'єкт нерухомості
   */
  property?: IProperty | null;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Обробник кліку по згортанню
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Можливість редагування
   */
  editable?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник кліку по кнопці Edit
   */
  onEdit?: () => void;
}

interface PropertyInfoField {
  label: string;
  value: string | number | null;
  type?: 'text' | 'number';
}

const baseClass = 'PropertyInformation';

const PropertyInformation: React.FC<PropertyInformationProps> = ({
                                                                   property,
                                                                   loading = false,
                                                                   collapsed,
                                                                   onToggleCollapse,
                                                                   editable = true,
                                                                   className = '',
                                                                   onEdit,
                                                                 }) => {
  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
  };

  // Базові характеристики будівлі
  const getBasicInfo = (): { left: PropertyInfoField[], right: PropertyInfoField[] } => {
    return {
      left: [
        { label: 'Construction year', value: property?.constructionYear ?? null, type: 'number' },
        { label: 'Foundation', value: property?.foundation ?? null },
        { label: 'Basement', value: property?.hasBasement ? (property.basementDimension ?? null) : null }
      ],
      right: [
        { label: 'Floors', value: property?.floors ?? null, type: 'number' },
        { label: 'Dimension', value: property?.dimension ?? null },
        { label: 'Exterior', value: property?.hasExterior ? (property.exteriorDimension ?? null) : null }
      ]
    };
  };

  // Обмеження та дозволи
  const getRestrictionsInfo = (): PropertyInfoField[] => {
    return [
      { label: 'Restrictions/Permissions', value: property?.hasRestrictions ? (property.restrictionsDescription ?? null) : null }
    ];
  };

  const basicInfo = getBasicInfo();
  const restrictionsInfo = getRestrictionsInfo();

  const renderDataField = (field: PropertyInfoField) => (
    <div className={`${baseClass}__data-field`}>
      {field.value ?? 'N/A'}
    </div>
  );

  const renderBasicSection = () => (
    <div className={`${baseClass}__section`}>
      <div className={`${baseClass}__columns`}>
        {/* Ліва колонка */}
        <div className={`${baseClass}__column`}>
          <div className={`${baseClass}__column-content`}>
            <div className={`${baseClass}__field-titles`}>
              {basicInfo.left.map((field, index) => (
                <div key={index} className={`${baseClass}__field-title`}>
                  {field.label}
                </div>
              ))}
            </div>
            <div className={`${baseClass}__field-values`}>
              {basicInfo.left.map((field, index) => (
                <div key={index} data-label={field.label}>
                  {renderDataField(field)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Права колонка */}
        <div className={`${baseClass}__column`}>
          <div className={`${baseClass}__column-content`}>
            <div className={`${baseClass}__field-titles`}>
              {basicInfo.right.map((field, index) => (
                <div key={index} className={`${baseClass}__field-title`}>
                  {field.label}
                </div>
              ))}
            </div>
            <div className={`${baseClass}__field-values`}>
              {basicInfo.right.map((field, index) => (
                <div key={index} data-label={field.label}>
                  {renderDataField(field)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRestrictionsSection = () => (
    <div className={`${baseClass}__section`}>
      <div className={`${baseClass}__restrictions`}>
        <span className={`${baseClass}__restrictions-label`}>
          {restrictionsInfo[0].label}
        </span>
        <div className={`${baseClass}__restrictions-value`} data-label={restrictionsInfo[0].label}>
          {restrictionsInfo[0].value ?? 'N/A'}
        </div>
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={`${baseClass}__content`}>
      {renderBasicSection()}
      {renderRestrictionsSection()}
      {editable && (
        <div className={`${baseClass}__actions`}>
          <UIIconButton
            variant="primary"
            size="medium"
            iconType="edit"
            text="Edit"
            onClick={handleEdit}
          />
        </div>
      )}
    </div>
  );

  return (
    <UICollapsibleSection
      title="Property information"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      headerActions={undefined}
      loading={loading}
      loadingText="Loading property information..."
      className={`${baseClass} ${className}`.trim()}
    >
      {renderContent()}
    </UICollapsibleSection>
  );
};

export default PropertyInformation;
